import React, { createContext, useContext, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { IJourney } from "../../interfaces/journey";
import api from "../../services/api";
import { useToast } from "../context/ToastContext";
import { resolveLogoUrl } from "../../Utils/resolveLogoUrl";
import { getUserIp } from "../../services/getUserIp";

export interface JourneyStyle {
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  buttonLabelColor: string;
  logoUrl?: string;
  screensJson?: string;
  initialText?: string;
  finalText?: string;
  redirectLink?: string;
}

type Step = "cpf" | "location" | "face" | "document" | "finish";

interface JourneyContextValue {
  styles: JourneyStyle;
  step: Step;
  next: () => void;
  previous: () => void;
  goTo: (s: Step) => void;
  journey?: IJourney;
  isLoading: boolean;
  cpf?: string;
  setJourney?: any;
  ip?: string;
  selfieImage?: any;
  setSelfieImage?: any;
  documentImage?: any;
  setDocumentImage?: any;
}
type ScreenConfig = { id: Step; enabled: boolean };

const JourneyContext = createContext<JourneyContextValue | null>(null);

export const useJourney = () => {
  const ctx = useContext(JourneyContext);
  if (!ctx) throw new Error("useJourney must be used inside JourneyProvider");
  return ctx;
};

export const JourneyProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const showToast = useToast();
  const { token } = useParams<{ token: string }>();

  const [journey, setJourney] = useState<IJourney | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [enabledSteps, setEnabledSteps] = useState<Step[]>([]);
  const [cpf, setCpf] = useState<string | undefined>("");
  const [ip, setIp] = useState<string>("");
  const [selfieImage, setSelfieImage] = useState<any>(null);
  const [documentImage, setDocumentImage] = useState<any>(null);

  const next = () =>
    setIndex((i) => Math.min(i + 1, Math.max(0, enabledSteps.length - 1)));
  const previous = () => setIndex((i) => (i - 1 >= 0 ? i - 1 : i));
  const goTo = (s: Step) => {
    const idx = enabledSteps.indexOf(s);
    if (idx !== -1) setIndex(idx);
  };

  function extractCpfFromJwt(token: string): string | null {
    try {
      const payload = token.split(".")[1];
      const decodedPayload = atob(payload);
      const parsed = JSON.parse(decodedPayload);
      return parsed?.cpf ?? null;
    } catch (err) {
      console.error("Erro ao decodificar o token:", err);
      return null;
    }
  }

  useEffect(() => {
    if (!token) {
      showToast("error", "Token ausente na URL.");
      return;
    }

    const extractedCpf = extractCpfFromJwt(token);
    setCpf(extractedCpf || "");

    type Step = "cpf" | "location" | "document" | "face" | "finish";

    const fetchJourneyByToken = async () => {
      try {
        const { data } = await api.get("/JourneyConfiguration/getByToken", {
          params: { token },
        });
        console.log("data", data);
        setJourney(data);

        const rawScreens: ScreenConfig[] = Array.isArray(data?.screens)
          ? data.screens
          : data?.screensJson
          ? JSON.parse(data.screensJson)
          : [];

        const parsedScreens = rawScreens
          .filter((s: any) => (s.enabled ?? s.Enabled) === true)
          .map((s: any) => s.id ?? s.Id) as Step[];

        const base: Step[] =
          parsedScreens.length > 0 ? parsedScreens : (["cpf"] as Step[]);

        const withoutFinish = base.filter((s) => s !== "finish");

        const unique = Array.from(new Set(withoutFinish)) as Step[];

        setEnabledSteps([...unique, "finish"]);
      } catch (err: any) {
        const status = err?.response?.status;
        switch (status) {
          case 401:
            showToast(
              "warning",
              "Link expirado ou inválido. Peça um novo link."
            );
            setJourney(undefined);
            break;
          case 404:
            showToast("error", "Jornada não encontrada. Verifique o link.");
            setJourney(undefined);
            break;
          default:
            showToast("error", "Link expirado ou inválido. Peça um novo link.");
            setJourney(undefined);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchJourneyByToken();
  }, [token]);

  useEffect(() => {
    const run = async () => {
      const ip = await getUserIp();
      setIp(ip || "");
    };
    run();
  }, []);

  const styles: JourneyStyle = journey
    ? {
        backgroundColor: journey.backgroundColor,
        textColor: journey.textColor,
        buttonColor: journey.buttonColor,
        buttonLabelColor: journey.buttonLabelColor,
        logoUrl: resolveLogoUrl(journey.logoUrl),
        screensJson: journey.screensJson,
        initialText: journey.initialText,
        finalText: journey.finalText,
        redirectLink: journey.redirectLink,
      }
    : {
        backgroundColor: "#fff",
        textColor: "#000",
        buttonColor: "#000",
        buttonLabelColor: "#fff",
        screensJson: "[]",
      };

  return (
    <JourneyContext.Provider
      value={{
        styles,
        step: enabledSteps[index] ?? "finish",
        next,
        previous,
        goTo,
        journey,
        isLoading,
        cpf,
        setJourney,
        ip,
        selfieImage,
        setSelfieImage,
        documentImage,
        setDocumentImage,
      }}
    >
      {children}
    </JourneyContext.Provider>
  );
};
